/**
 * Strava API Service
 * Handles direct API calls to Strava from the frontend
 */

import { getFunctions, httpsCallable } from "firebase/functions";

const STRAVA_API_BASE = 'https://www.strava.com/api/v3';

class StravaApiService {
    constructor() {
        this.rateLimitInfo = {
            limit: 200,
            usage: 0,
            resetTime: null
        };
    }

    /**
     * Get routes from Strava API
     * @param {string} accessToken - Strava access token
     * @param {number} page - Page number for pagination
     * @param {number} perPage - Items per page (max 200)
     * @returns {Promise<Object>} Strava API response
     */
    async getRoutes(accessToken, page = 1, perPage = 200) {
        try {
            const response = await fetch(
                `${STRAVA_API_BASE}/athlete/routes?page=${page}&per_page=${perPage}`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            // Update rate limit info from response headers
            this.updateRateLimitInfo(response);

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('Strava token expired. Please reconnect your Strava account.');
                }
                if (response.status === 429) {
                    throw new Error('Rate limit exceeded. Please try again later.');
                }
                throw new Error(`Strava API error: ${response.status}`);
            }

            const data = await response.json();
            return {
                routes: data,
                rateLimitInfo: this.rateLimitInfo
            };
        } catch (error) {
            console.error('Error fetching Strava routes:', error);
            throw error;
        }
    }

    /**
     * Get a specific route by ID from Strava API
     * @param {string} accessToken - Strava access token
     * @param {string} routeId - Strava route ID
     * @returns {Promise<Object>} Strava route data
     */
    async getRoute(accessToken, routeId) {
        try {
            const response = await fetch(
                `${STRAVA_API_BASE}/routes/${routeId}`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            this.updateRateLimitInfo(response);

            if (!response.ok) {
                if (response.status === 429) {
                    throw new Error('Rate limit exceeded. Please try again later.');
                }
                throw new Error(`Strava API error: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching Strava route:', error);
            throw error;
        }
    }

    /**
     * Export route as GPX from Strava API
     * @param {string} accessToken - Strava access token
     * @param {string} routeId - Strava route ID
     * @returns {Promise<string>} GPX data as string
     */
    async exportRouteAsGPX(accessToken, routeId) {
        try {
            const response = await fetch(
                `${STRAVA_API_BASE}/routes/${routeId}/export_gpx`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Accept': 'application/gpx+xml'
                    }
                }
            );

            this.updateRateLimitInfo(response);

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('Strava token expired. Please reconnect your Strava account.');
                }
                if (response.status === 404) {
                    throw new Error('Route not found or GPX export not available.');
                }
                if (response.status === 429) {
                    throw new Error('Rate limit exceeded. Please try again later.');
                }
                throw new Error(`Strava API error: ${response.status}`);
            }

            return await response.text();
        } catch (error) {
            console.error('Error exporting Strava route as GPX:', error);
            throw error;
        }
    }

    /**
     * Get route streams (elevation, distance, etc.) from Strava API
     * @param {string} accessToken - Strava access token
     * @param {string} routeId - Strava route ID
     * @returns {Promise<Object>} Strava route streams data
     */
    async getRouteStreams(accessToken, routeId) {
        try {
            const response = await fetch(
                `${STRAVA_API_BASE}/routes/${routeId}/streams/elevation,distance,latlng`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            this.updateRateLimitInfo(response);

            if (!response.ok) {
                if (response.status === 429) {
                    throw new Error('Rate limit exceeded. Please try again later.');
                }
                throw new Error(`Strava API error: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching Strava route streams:', error);
            throw error;
        }
    }

    /**
     * Update rate limit information from API response headers
     * @param {Response} response - Fetch response object
     */
    updateRateLimitInfo(response) {
        const limit = response.headers.get('X-RateLimit-Limit');
        const usage = response.headers.get('X-RateLimit-Usage');
        const resetTime = response.headers.get('X-RateLimit-Reset');

        if (limit) this.rateLimitInfo.limit = parseInt(limit);
        if (usage) this.rateLimitInfo.usage = parseInt(usage);
        if (resetTime) this.rateLimitInfo.resetTime = new Date(parseInt(resetTime) * 1000);
    }

    /**
     * Check if rate limit is approaching
     * @returns {boolean} True if rate limit is close to being exceeded
     */
    isRateLimitApproaching() {
        return this.rateLimitInfo.usage > (this.rateLimitInfo.limit * 0.8);
    }

    /**
     * Get time until rate limit resets
     * @returns {number} Milliseconds until reset
     */
    getTimeUntilReset() {
        if (!this.rateLimitInfo.resetTime) return 0;
        return this.rateLimitInfo.resetTime.getTime() - Date.now();
    }

    /**
     * Refresh an expired access token using refresh token
     * @param {string} refreshToken - Strava refresh token
     * @returns {Promise<Object>} New token data
     */
    async refreshAccessToken(refreshToken) {
        try {
            const functions = getFunctions();
            const refreshTokenFn = httpsCallable(functions, 'strava_refresh_access_token');

            const result = await refreshTokenFn({ refresh_token: refreshToken });
            return JSON.parse(result.data);
        } catch (error) {
            console.error('Error refreshing Strava token:', error);
            throw error;
        }
    }

    /**
     * Check if access token is expired or will expire soon
     * @param {Object} tokenData - Token data with expires_at
     * @returns {boolean} True if token needs refresh
     */
    isTokenExpired(tokenData) {
        if (!tokenData || !tokenData.expires_at) return true;

        // Check if token expires in less than 1 hour (3600 seconds)
        const now = Math.floor(Date.now() / 1000);
        const expiresAt = tokenData.expires_at;
        const timeUntilExpiry = expiresAt - now;

        return timeUntilExpiry <= 3600;
    }

    /**
     * Map Strava route data to your application's route format
     * @param {Object} stravaRoute - Raw Strava route data
     * @returns {Object} Mapped route data
     */
    mapStravaRoute(stravaRoute) {
        // Use id_str if available to avoid JavaScript number precision loss for large integers
        const routeId = stravaRoute.id_str || String(stravaRoute.id);
        return {
            id: routeId,
            name: stravaRoute.name,
            description: stravaRoute.description || '',
            distance: stravaRoute.distance,
            elevation_gain: stravaRoute.elevation_gain,
            created_at: stravaRoute.created_at,
            updated_at: stravaRoute.updated_at,
            source: 'strava',
            source_id: stravaRoute.id_str || stravaRoute.id,
            // Map Strava-specific fields
            strava_data: {
                sub_type: stravaRoute.sub_type,
                timestamp: stravaRoute.timestamp,
                type: stravaRoute.type,
                // Add other Strava-specific fields as needed
            }
        };
    }
}

// Export singleton instance
const stravaApiService = new StravaApiService();
export default stravaApiService;
